'use client';

import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Article {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  author: string;
  authorRole: string;
  publishDate: string;
  readTime: string;
  image: string;
  alt: string;
  tags: string[];
}

interface FeaturedArticleProps {
  article: Article;
}

const FeaturedArticle = ({ article }: FeaturedArticleProps) => {
  return (
    <div className="bg-white rounded-2xl border border-border shadow-brand-lg overflow-hidden group">
      <div className="grid lg:grid-cols-2 gap-0">
        <div className="relative h-64 lg:h-full overflow-hidden">
          <AppImage
            src={article.image}
            alt={article.alt}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute top-4 left-4">
            <span className="px-4 py-2 bg-accent text-white font-bold rounded-full text-sm shadow-lg">
              Featured
            </span>
          </div>
        </div>
        <div className="p-8 lg:p-10 flex flex-col justify-center">
          <div className="flex items-center gap-2 mb-4">
            <span className="px-3 py-1 bg-primary/10 text-primary font-bold rounded-full text-xs">
              {article.category.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
            </span>
            <span className="text-muted-foreground text-sm">•</span>
            <span className="text-muted-foreground text-sm">{article.readTime}</span>
          </div>
          <h2 className="font-heading text-3xl lg:text-4xl font-black text-foreground mb-4 group-hover:text-primary transition-colors">
            {article.title}
          </h2>
          <p className="text-muted-foreground text-lg mb-6 line-clamp-3">
            {article.excerpt}
          </p>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white font-black">
                {article.author.split(' ').map(n => n[0]).join('')}
              </div>
              <div>
                <p className="font-bold text-foreground">{article.author}</p>
                <p className="text-sm text-muted-foreground">{article.authorRole}</p>
              </div>
            </div>
            <button className="px-6 py-3 bg-primary text-white font-bold rounded-lg hover:bg-secondary transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2">
              Read More
              <Icon name="ArrowRightIcon" size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeaturedArticle;